import { Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useChooseAuthority, useMyAuthorities } from "@/auth/authorities";
import { Empty, LoadFailed, Loading } from "@/components/states";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/i18n";

import { AuthLayout } from "./auth-layout";

/** Picking the authority to work for, when there is more than one. */
export function ChooseAuthorityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authorities = useMyAuthorities();
  const choose = useChooseAuthority();

  let body;
  if (authorities.isPending) {
    body = <Loading />;
  } else if (authorities.isError) {
    body = (
      <LoadFailed
        error={authorities.error}
        onRetry={() => {
          void authorities.refetch();
        }}
      />
    );
  } else if (authorities.data.length === 0) {
    body = <Empty title={t("authority.noneTitle")}>{t("authority.noneBody")}</Empty>;
  } else {
    body = (
      <ul className="flex flex-col gap-3">
        {authorities.data.map((authority) => (
          <li key={authority.id}>
            <Button
              variant="secondary"
              className="h-auto w-full justify-start py-3 text-left"
              disabled={choose.isPending}
              onClick={() => {
                choose.mutate(authority.id, {
                  onSuccess: () => {
                    void navigate("/", { replace: true });
                  },
                });
              }}
            >
              <Building2 aria-hidden="true" className="size-5 shrink-0" />
              <span className="flex flex-col">
                <span>{authority.name}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {t(`roles.${authority.role}`)} ·{" "}
                  {t("authority.state", { state: authority.stateName })}
                </span>
              </span>
            </Button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <AuthLayout title={t("authority.chooseTitle")} intro={t("authority.chooseIntro")}>
      {choose.isError ? (
        <div className="mb-4">
          <Alert tone="error">{errorMessage(choose.error)}</Alert>
        </div>
      ) : null}
      {body}
    </AuthLayout>
  );
}
